import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/system'
import { factories, IEmbedConfiguration, models, service } from 'powerbi-client'
import Spinner from 'src/components/forms/Spinner'
import api from '../../api'
import tokens from '../../styles/tokens.json'

const MINUTES_BEFORE_EXPIRATION = 10 // 10 minutes before expiration to refresh token

// To convert hex to rgba for Shadow effect
const hexToRgba = (hex: string, alpha: number) =>
  `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(
    hex.slice(3, 5),
    16,
  )}, ${parseInt(hex.slice(5, 7), 16)}, ${alpha})`

const styles = {
  content: {
    display: 'flex',
    justifyContent: 'center',
  },
  reportContainer: {
    height: '51.25rem',
    width: '100%',
    minWidth: '81.25rem',
    backgroundColor: tokens.colors.backgroundGray,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    position: 'relative',
    boxShadow: `0 0.125rem 0.625rem ${hexToRgba(tokens.colors.black, 0.1)}`,
  },
}

interface PowerBIReportProps {
  reportName: string | undefined
}

const PowerBIReport = ({ reportName }: PowerBIReportProps) => {
  const reportDiv = useRef<HTMLDivElement>(null)
  const [token, setToken] = useState<string>('')
  const [embedUrl, setEmbedUrl] = useState<string>('')
  const [reportId, setReportId] = useState<string>('')
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [tokenExpiration, setTokenExpiration] = useState<string>('')
  const powerbi = new service.Service(
    factories.hpmFactory,
    factories.wpmpFactory,
    factories.routerFactory,
  )

  // Fetch report config from API
  async function getReportData() {
    try {
      const response = await api.user.report_GetReportClientByName(reportName)
      const data = response?.data
      return {
        reportId: data?.reportId ?? '',
        embedUrl: data?.embedUrl ?? '',
        accessToken: data?.accessToken ?? '',
        tokenExpiration: data?.tokenExpiration ?? '', // UTC
      }
    } catch (error) {
      console.error('Failed to fetch report data:', error)
      return {
        reportId: '',
        embedUrl: '',
        accessToken: '',
        tokenExpiration: '',
      }
    }
  }

  // Refresh token and apply to embedded report
  async function updateToken() {
    const newAccessToken = await getReportData()
    const reportContainer = reportDiv.current

    if (reportContainer && newAccessToken) {
      const report = powerbi.get(reportContainer)
      setTokenExpiration(newAccessToken.tokenExpiration || '')
      await report.setAccessToken(newAccessToken.accessToken || '')
    }
  }

  // Initial fetch on mount
  useEffect(() => {
    const getReportInfo = async () => {
      const report = await getReportData()
      setEmbedUrl(report.embedUrl)
      setToken(report.accessToken)
      setReportId(report.reportId)
      setTokenExpiration(report.tokenExpiration)
      setIsLoaded(true)
    }
    getReportInfo()
  }, [])

  // Embed the report once data is ready
  useEffect(() => {
    if (!embedUrl || !token || !reportId) return
    const reportContainer = reportDiv.current
    if (reportContainer) {
      const embedConfig: IEmbedConfiguration = {
        type: 'report',
        id: reportId,
        embedUrl,
        accessToken: token,
        tokenType: models.TokenType.Embed,
      }
      const report = powerbi.embed(reportContainer, embedConfig)
      report.off('loaded')
      report.on('loaded', () => console.log('Report has loaded'))
    }
  }, [embedUrl, token, reportId])

  // Token refresh logic using setTimeout
  useEffect(() => {
    if (!tokenExpiration) return undefined

    const expirationTime = Date.parse(tokenExpiration)
    const currentTime = Date.now()
    const bufferTime = MINUTES_BEFORE_EXPIRATION * 60 * 1000
    const refreshDelay = expirationTime - currentTime - bufferTime

    if (refreshDelay <= 0) {
      updateToken()
      return undefined
    }

    const timeoutId = setTimeout(() => {
      updateToken()
    }, refreshDelay)

    const visibilityHandler = () => {
      if (!document.hidden) {
        const now = Date.now()
        if (now >= expirationTime - bufferTime) {
          updateToken()
        }
      }
    }

    document.addEventListener('visibilitychange', visibilityHandler)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('visibilitychange', visibilityHandler)
    }
  }, [tokenExpiration])

  return (
    <Box sx={styles.content}>
      <Box ref={reportDiv} sx={styles.reportContainer}>
        <Spinner open={!isLoaded} />
      </Box>
    </Box>
  )
}

export default PowerBIReport
